import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DeleteButton from './ui/DeleteButton';
import {
  Plus, Trash2, GripVertical, Music, ChevronLeft, Paperclip,
  Download, Upload, ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const ItemDetailView = ({
  item, hasEditPermission, itemDetailTab, setItemDetailTab,
  updateSelectedItem, setSelectedItemId,  // kept for compat; back nav uses history
  // Songs
  getSongMeta, setFormData, selectedItemId, openSongPicker,
  removeSongFromItem,
  // Attachments
  uploading, handleFileUpload, removeAttachment,
  // Links
  openAddLinkDialog, removeLink,
  // Helpers
  formatFileSize, getDisplayUrl, onSongDragEnd,
}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const notesRef = useRef(null);

  useEffect(() => {
    const el = notesRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [item?.notes]);

  if (!item) return null;
  const itemSongs = item.songs || [];

  return (
    <div data-testid="item-detail-view">
      <button onClick={() => navigate(-1)} data-testid="back-to-order-btn"
        className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors mb-5">
        <ChevronLeft className="w-4 h-4" /> Back to Program
      </button>
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5">
        <div>
          <Label htmlFor="detail-name">Name</Label>
          <Input id="detail-name" data-testid="item-detail-name" value={item.name || ''}
            onChange={(e) => updateSelectedItem('name', e.target.value)} disabled={!hasEditPermission} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="detail-notes">Notes</Label>
          <Textarea id="detail-notes" ref={notesRef} data-testid="item-detail-notes" value={item.notes || ''}
            onChange={(e) => updateSelectedItem('notes', e.target.value)} disabled={!hasEditPermission} rows={2} className="mt-1 resize-none overflow-hidden" />
        </div>
        <div>
          {/* Item sub-tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-4" data-testid="item-detail-tabs">
            {[
              { id: 'songs', label: 'Songs', icon: Music, count: itemSongs.length },
              { id: 'attachments', label: 'Files', icon: Paperclip, count: (item.attachments || []).length },
              { id: 'links', label: 'Links', icon: ExternalLink, count: (item.links || []).length },
            ].map(tab => (
              <button key={tab.id} onClick={() => setItemDetailTab(tab.id)} data-testid={`item-tab-${tab.id}`}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  itemDetailTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}>
                <tab.icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{tab.label}</span>
                {tab.count > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${itemDetailTab === tab.id ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>{tab.count}</span>}
              </button>
            ))}
          </div>

          {/* Songs Tab */}
          {itemDetailTab === 'songs' && (
            <div data-testid="item-songs-tab">
              {hasEditPermission && (
                <div className="flex justify-end gap-2 mb-3">
                  <Button variant="outline" size="sm" data-testid="add-song-btn"
                    className="rounded-full gap-1.5 text-xs h-8" onClick={openSongPicker}>
                    <Plus className="w-3.5 h-3.5" /><span className="hidden sm:inline">Add Song</span>
                  </Button>
                </div>
              )}
              {itemSongs.length === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center">
                  <Music className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No songs added yet.</p>
                  {hasEditPermission && (
                    <button type="button" onClick={openSongPicker} data-testid="add-song-empty-btn"
                      className="mt-2 text-sm text-green-600 hover:text-green-800 font-medium">Add a song from setlist or library</button>
                  )}
                </div>
              ) : (
                <DragDropContext onDragEnd={onSongDragEnd}>
                  <Droppable droppableId="item-songs">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2" data-testid="detail-songs-list">
                        {itemSongs.map((song, idx) => {
                          const meta = getSongMeta(song.song_id);
                          const title = song.title || meta.title || 'Unknown';
                          const artist = song.artist || meta.artist || '';
                          // Key comes from setlist via getSongMeta (which merges setlist overrides)
                          const songKey = meta.key || '';
                          return (
                            <Draggable key={song.song_id} draggableId={song.song_id} index={idx} isDragDisabled={!hasEditPermission}>
                              {(dp, snap) => (
                                <div ref={dp.innerRef} {...dp.draggableProps}
                                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm ${snap.isDragging ? 'bg-green-50 border-green-200 shadow-md' : 'bg-slate-50 border-slate-200'}`}
                                  data-testid={`detail-song-${song.song_id}`}>
                                  {hasEditPermission && <div {...dp.dragHandleProps} className="cursor-grab active:cursor-grabbing flex-shrink-0"><GripVertical className="w-4 h-4 text-slate-300" /></div>}
                                  <Music className="w-4 h-4 text-green-400 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-slate-900">{title}</p>
                                    <p className="text-xs text-slate-500">{artist}</p>
                                  </div>
                                  {/* Key read-only badge (managed in Setlist tab) */}
                                  {songKey && (
                                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-mono font-semibold rounded shrink-0">{songKey}</span>
                                  )}
                                  {hasEditPermission && (
                                    <DeleteButton
                                      onConfirm={() => removeSongFromItem(song.song_id)}
                                      className="p-1 flex-shrink-0"
                                      title="Remove song"
                                      testId={`remove-song-${song.song_id}`}
                                    />
                                  )}
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          )}

          {/* Attachments Tab */}
          {itemDetailTab === 'attachments' && (
            <div data-testid="item-attachments-tab">
              {hasEditPermission && (
                <div className="flex justify-end mb-3">
                  <Button variant="outline" size="sm" data-testid="add-attachment-btn"
                    className="rounded-full gap-1.5 text-xs h-8" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    {uploading ? <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current" /><span className="hidden sm:inline"> Uploading...</span></> : <><Upload className="w-3.5 h-3.5" /><span className="hidden sm:inline"> Add File</span></>}
                  </Button>
                </div>
              )}
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileUpload} data-testid="attachment-file-input" />
              {(item.attachments || []).length === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center">
                  <Paperclip className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No attachments yet.</p>
                  {hasEditPermission && (
                    <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="add-attachment-empty-btn"
                      className="mt-2 text-sm text-green-600 hover:text-green-800 font-medium">Upload a file</button>
                  )}
                </div>
              ) : (
                <div className="space-y-1" data-testid="attachments-list">
                  {(item.attachments || []).map((att) => (
                    <div key={att.attachment_id} className="flex items-center gap-3 px-4 py-3 rounded-lg border bg-slate-50 border-slate-200" data-testid={`attachment-${att.attachment_id}`}>
                      <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{att.original_name}</p>
                        <p className="text-xs text-slate-500">{formatFileSize(att.size)}</p>
                      </div>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${att.url}`} download={att.original_name} target="_blank" rel="noopener noreferrer"
                        className="p-1 text-slate-400 hover:text-green-600 transition-colors" data-testid={`download-attachment-${att.attachment_id}`}
                        onClick={(e) => e.stopPropagation()}>
                        <Download className="w-4 h-4" />
                      </a>
                      {hasEditPermission && (
                        <DeleteButton
                          onConfirm={() => removeAttachment(att.attachment_id)}
                          className="p-1"
                          title="Remove attachment"
                          testId={`remove-attachment-${att.attachment_id}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Links Tab */}
          {itemDetailTab === 'links' && (
            <div data-testid="item-links-tab">
              {hasEditPermission && (
                <div className="flex justify-end mb-3">
                  <Button variant="outline" size="sm" data-testid="add-link-btn"
                    className="rounded-full gap-1.5 text-xs h-8" onClick={openAddLinkDialog}>
                    <Plus className="w-3.5 h-3.5" /><span className="hidden sm:inline"> Add Link</span>
                  </Button>
                </div>
              )}
              {(item.links || []).length === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center">
                  <ExternalLink className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No links yet.</p>
                  {hasEditPermission && (
                    <button type="button" onClick={openAddLinkDialog} data-testid="add-link-empty-btn"
                      className="mt-2 text-sm text-green-600 hover:text-green-800 font-medium">Add a link</button>
                  )}
                </div>
              ) : (
                <div className="space-y-1" data-testid="links-list">
                  {(item.links || []).map((link) => (
                    <div key={link.id} className="flex items-center gap-3 px-4 py-3 rounded-lg border bg-slate-50 border-slate-200" data-testid={`link-${link.id}`}>
                      <ExternalLink className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{link.label || getDisplayUrl(link.url)}</p>
                        {link.label && <p className="text-xs text-slate-500 truncate">{link.url}</p>}
                      </div>
                      <a href={link.url} target="_blank" rel="noopener noreferrer"
                        className="p-1 text-slate-400 hover:text-green-600 transition-colors" data-testid={`open-link-${link.id}`}>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      {hasEditPermission && (
                        <DeleteButton
                          onConfirm={() => removeLink(link.id)}
                          className="p-1"
                          title="Remove link"
                          testId={`remove-link-${link.id}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemDetailView;
